import React, { useEffect, useState } from 'react';
import { ResizableTable } from './ResizableTable';
import { AiCategorizationFields, CATEGORY_LABELS, URGENCY_COLORS } from './feedbackTypes';
import './BugReportsSection.scss';

export interface FeedbackPromptResponse extends AiCategorizationFields {
    responseId: string;
    createdAt: string;
    userId: string;
    triggerId: string;
    question: string;
    response: string | null;
    followUpText: string | null;
}

export type FeedbackPromptResponsesSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

// Milestone-triggered in-app survey responses — separate from the free-form
// "Share Feedback" submissions shown in FeedbackSubmissionsSection. Read-only:
// there's no status workflow for these, just a scan-and-review view for now.
export const FeedbackPromptResponsesSection: React.FC<FeedbackPromptResponsesSectionProps> = ({ API_BASE, authHeaders }) => {
    const [responses, setResponses] = useState<FeedbackPromptResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState<'createdAt' | 'userId' | 'triggerId'>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedResponse, setSelectedResponse] = useState<FeedbackPromptResponse | null>(null);

    const fetchResponses = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/feedback-prompts/responses`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to fetch feedback prompt responses.');
            }
            const data = await res.json();
            setResponses(Array.isArray(data.items) ? data.items : []);
        } catch (err: any) {
            setError(err.message || 'Error retrieving feedback prompt responses.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResponses();
    }, []);

    const responseColumns = {
        responseId: 30,
        userId: 30,
        createdAt: 30,
        triggerId: 140,
        aiCategory: 140,
        question: 260,
        response: 120,
        followUpText: 320,
    };

    const sortedResponses = [...responses].sort((a, b) => {
        const fieldA = a[sortField] ?? '';
        const fieldB = b[sortField] ?? '';
        if (fieldA === fieldB) return 0;
        if (sortOrder === 'asc') {
            return String(fieldA) > String(fieldB) ? 1 : -1;
        } else {
            return String(fieldA) < String(fieldB) ? 1 : -1;
        }
    });

    return (
        <section className="table-section" style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <h2>Feedback Prompt Responses</h2>
            <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                <ResizableTable
                    columns={responseColumns}
                    data={sortedResponses}
                    onRefresh={fetchResponses}
                    refreshButton={true}
                    sortable={true}
                    sortableColumns={['responseId', 'userId', 'createdAt', 'triggerId', 'aiCategory', 'question', 'response', 'followUpText']}
                    defaultSortField="createdAt"
                    defaultSortOrder="desc"
                    submitButton={false}
                    deleteButton={false}
                    onRowClick={row => setSelectedResponse(row as FeedbackPromptResponse)}
                />
            </div>
            {error && <span style={{ color: 'red', marginLeft: 16 }}>{error}</span>}
            {loading && <span style={{ marginLeft: 16 }}>Processing...</span>}

            {selectedResponse && (
                <div className="bug-reports-modal-overlay" onClick={() => setSelectedResponse(null)}>
                    <div className="bug-reports-modal" onClick={e => e.stopPropagation()}>
                        <h3>Feedback Prompt Response</h3>
                        <div className="bug-reports-modal-field">
                            <label>Response ID</label>
                            <p>{selectedResponse.responseId}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>User ID</label>
                            <p>{selectedResponse.userId}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Submitted</label>
                            <p>{new Date(selectedResponse.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Trigger</label>
                            <p>{selectedResponse.triggerId}</p>
                        </div>
                        {selectedResponse.aiCategory && (
                            <div className="bug-reports-modal-field">
                                <label>AI Category</label>
                                <p>{CATEGORY_LABELS[selectedResponse.aiCategory] || selectedResponse.aiCategory}</p>
                            </div>
                        )}
                        {selectedResponse.aiUrgency && (
                            <div className="bug-reports-modal-field">
                                <label>AI Urgency</label>
                                <p style={{ color: URGENCY_COLORS[selectedResponse.aiUrgency], fontWeight: 600 }}>
                                    {selectedResponse.aiUrgency.toUpperCase()}
                                </p>
                            </div>
                        )}
                        <div className="bug-reports-modal-field">
                            <label>Question</label>
                            <p>{selectedResponse.question}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Response</label>
                            <p>{selectedResponse.response || '—'}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Follow-up</label>
                            <p>{selectedResponse.followUpText || '—'}</p>
                        </div>
                        <button className="bug-reports-modal-close" onClick={() => setSelectedResponse(null)}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};
